/* SOLEVIA video reviews carousel: 3 cards per view on desktop, 2 on tablet, 1 on phones,
   an arrow on each side, swipe/scroll by hand, videos that play (muted) only while visible,
   and an enlarge view with sound and controls. Without JavaScript the row still scrolls by hand. */
(() => {
  'use strict';
  if (customElements.get('sv-reel')) return;
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class SvReel extends HTMLElement {
    connectedCallback() {
      this.vp = this.querySelector('[data-reel-viewport]');
      this.prev = this.querySelector('[data-reel-prev]');
      this.next = this.querySelector('[data-reel-next]');
      this.lb = this.querySelector('[data-reel-lb]');
      this.lbMedia = this.querySelector('[data-reel-lb-media]');
      this.lbCopy = this.querySelector('[data-reel-lb-copy]');
      this.visible = new Set();
      this.lbOpen = false;

      this.onPrev = () => this.go(-1);
      this.onNext = () => this.go(1);
      this.onScroll = () => this.update();
      this.onResize = () => this.update();
      this.onClick = (e) => {
        if (e.target.closest('[data-reel-lb-close]') || e.target === this.lb) { this.close(); return; }
        const z = e.target.closest('[data-reel-zoom]');
        if (z) { e.preventDefault(); this.open(z.closest('[data-reel-card]'), z); }
      };
      this.onLbClose = () => this.finish();
      this.onEditorSelect = (e) => {
        const card = e.target && e.target.closest && e.target.closest('[data-reel-card]');
        if (card && this.vp && this.vp.contains(card)) this.vp.scrollTo({ left: card.offsetLeft - this.vp.firstElementChild.offsetLeft, behavior: 'auto' });
      };

      if (this.prev) this.prev.addEventListener('click', this.onPrev);
      if (this.next) this.next.addEventListener('click', this.onNext);
      if (this.vp) this.vp.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.addEventListener('click', this.onClick);
      if (this.lb) this.lb.addEventListener('close', this.onLbClose);
      document.addEventListener('shopify:block:select', this.onEditorSelect);

      // Card videos play muted only while on screen.
      this.videos = Array.from(this.querySelectorAll('.sv-reel__card video'));
      if ('IntersectionObserver' in window && this.videos.length) {
        this.io = new IntersectionObserver((entries) => {
          entries.forEach((en) => { if (en.isIntersecting) this.visible.add(en.target); else { this.visible.delete(en.target); en.target.pause(); } });
          this.sync();
        }, { threshold: 0.5 });
        this.videos.forEach((v) => this.io.observe(v));
      }
      if ('ResizeObserver' in window && this.vp) { this.ro = new ResizeObserver(() => this.update()); this.ro.observe(this.vp); }
      this.update();
    }
    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.ro) this.ro.disconnect();
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:block:select', this.onEditorSelect);
    }
    step() {
      const cards = this.vp ? this.vp.querySelectorAll('[data-reel-card]') : [];
      if (cards.length > 1) return cards[1].offsetLeft - cards[0].offsetLeft;
      return this.vp ? this.vp.clientWidth : 0;
    }
    go(dir) {
      if (!this.vp) return;
      const btn = dir < 0 ? this.prev : this.next;
      if (btn && btn.getAttribute('aria-disabled') === 'true') return;
      this.vp.scrollBy({ left: dir * this.step(), behavior: reduced() ? 'auto' : 'smooth' });
    }
    update() {
      if (!this.vp) return;
      const max = this.vp.scrollWidth - this.vp.clientWidth;
      const x = this.vp.scrollLeft;
      if (this.prev) this.prev.setAttribute('aria-disabled', x <= 2 ? 'true' : 'false');
      if (this.next) this.next.setAttribute('aria-disabled', x >= max - 2 ? 'true' : 'false');
      // Keep the arrows level with the middle of the card media.
      const media = this.vp.querySelector('.sv-reel__media');
      if (media) this.style.setProperty('--reel-arrow-y', `${media.offsetHeight / 2}px`);
    }
    sync() {
      const allowPlay = !this.lbOpen && !reduced();
      this.visible.forEach((v) => {
        if (allowPlay) { const p = v.play(); if (p && p.catch) p.catch(() => {}); } else v.pause();
      });
    }
    open(card, opener) {
      if (!this.lb || !card) return;
      this.opener = opener;
      this.lbMedia.innerHTML = '';
      this.lbCopy.innerHTML = '';
      const video = card.querySelector('video');
      const media = card.querySelector('.sv-reel__media');
      if (video) {
        const v = video.cloneNode(true);
        v.removeAttribute('class');
        v.controls = true; v.muted = false; v.loop = false; v.preload = 'auto'; v.playsInline = true;
        this.lbMedia.appendChild(v);
      } else if (media && media.dataset.zoomSrc) {
        const img = document.createElement('img');
        img.src = media.dataset.zoomSrc; img.alt = media.dataset.zoomAlt || '';
        this.lbMedia.appendChild(img);
      }
      const copy = card.querySelector('[data-reel-copy]');
      if (copy) this.lbCopy.appendChild(copy.cloneNode(true));
      this.lbOpen = true;
      this.sync();
      try { this.lb.showModal(); } catch (e) { this.lb.setAttribute('open', ''); }
      if (window.Solevia && window.Solevia.lock) window.Solevia.lock('reel');
      const v = this.lbMedia.querySelector('video');
      if (v) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
      const c = this.lb.querySelector('[data-reel-lb-close]');
      if (c) c.focus({ preventScroll: true });
    }
    close() { if (this.lb && this.lb.open) this.lb.close(); else this.finish(); }
    finish() {
      if (!this.lbOpen) return;
      this.lbOpen = false;
      const v = this.lbMedia && this.lbMedia.querySelector('video');
      if (v) v.pause();
      if (this.lbMedia) this.lbMedia.innerHTML = '';
      if (this.lbCopy) this.lbCopy.innerHTML = '';
      if (window.Solevia && window.Solevia.unlock) window.Solevia.unlock('reel');
      this.sync();
      const o = this.opener;
      this.opener = null;
      if (o && o.isConnected) o.focus({ preventScroll: true });
    }
  }
  customElements.define('sv-reel', SvReel);
})();
